'use client'

import {
	CONTEXTUAL_MESSAGE_TYPE,
	ContextualMessage,
} from '@fukku/contextual-message/ContextualMessage'
import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useEffect, useRef, useState } from 'react'
import { cx } from 'utils/cx'
import { useModelInfo } from '../hooks/useModelInfo'
import type { Color, Look } from '../types/Color'

import styles from './ModelInfo.module.css'

interface ModelInfoProps {
	readonly selectedColor?: Color
	readonly selectedLook?: Look
	readonly className?: string
}

const ShowModelEvent = 'show_model_info'

export const ModelInfo = ({
	className,
	selectedColor,
	selectedLook,
}: ModelInfoProps) => {
	const { t } = useLabels()
	const { modelHeight, modelSizeCode } = useModelInfo({
		selectedColor,
		selectedLook,
	})
	const ref = useRef<HTMLDivElement | null>(null)
	const entry = useIntersectionObserver(ref, {
		threshold: 0,
	})
	const [sentAnalytics, setSentAnalytics] = useState(false)

	let message = ''

	const showModelInfo = modelHeight && modelSizeCode

	useEffect(() => {
		if (
			!sentAnalytics &&
			!selectedColor?.disclaimer &&
			showModelInfo &&
			entry?.isIntersecting
		) {
			sendGenericEvent(ShowModelEvent)
			setSentAnalytics(true)
		}
	}, [selectedColor, showModelInfo, entry, sentAnalytics])

	if (selectedColor?.disclaimer || !showModelInfo) {
		return null
	}

	if (modelHeight && modelSizeCode) {
		const placeholderValues = {
			1: modelSizeCode,
			2: modelHeight,
		}
		message = replacePlaceholders(
			t('pdp.sizeSelector.modelMeasurements.text'),
			placeholderValues
		)
	}

	return (
		<div className={cx(styles.modelInfo, className)} ref={ref}>
			<ContextualMessage
				className={styles.message}
				type={CONTEXTUAL_MESSAGE_TYPE.DEFAULT}
				message={message}
			/>
		</div>
	)
}
